import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Button from '../components/ui/Button.tsx';
import GradientText from '../components/ui/GradientText.tsx';
import SEO from '../components/SEO.tsx';
import { XCircle, RefreshCw, Loader } from 'lucide-react';

interface CancelProps {
  onNavigate: (page: string) => void;
}

const Cancel: React.FC<CancelProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(true);
  const currentPlan = new URLSearchParams(location.search).get('plan') || 'Premium';

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsProcessing(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  if (isProcessing) {
    return (
      <div className="min-h-screen pt-16">
        <SEO
          title="Processing Payment - 4urCloud Kexa"
          description="Processing your payment for Kexa Premium"
          keywords="payment processing, kexa premium, cloud security"
        />
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white rounded-2xl shadow-modern p-12">
              <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader className="w-8 h-8 text-white animate-spin" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Processing Your Payment
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Please wait while we confirm your payment and update your account...
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      <SEO
        title="Payment Cancelled - 4urCloud Kexa"
        description="Your payment was cancelled. You can try again anytime."
        keywords="payment cancelled, kexa premium, cloud security"
      />
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl shadow-modern p-12 animate-slide-up">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <GradientText variant="primary">Payment Cancelled!</GradientText>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Your payment for Kexa {currentPlan} was cancelled. No charges were made to your account.
            </p>


            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => onNavigate('pricing')}
                variant="primary"
                size="lg"
                leftIcon={<RefreshCw size={20} />}
              >
                Try Again
              </Button>
              <Button
                onClick={() => onNavigate('home')}
                variant="outline"
                size="lg"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cancel;